import { RankKit } from "@/components/site/RankKit";
import { NetworkCallout } from "@/components/site/BrandNetwork";
import { createFileRoute } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { TableOfContents, Section, SectionHeader, CTABand, FAQ, StatRow, Breadcrumbs, InlineCTA, FeatureGrid } from "@/components/site/Blocks";
import { ProsCons, TLDR, ExpertInsight } from "@/components/site/SeoBlocks";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { DscrProgramLinks } from "@/components/site/DscrProgramLinks";
import { DscrCrossProductLinks } from "@/components/site/DscrCrossProductLinks";
import { getDscrClusterHero } from "@/data/dscr-cluster-heroes";
import { HelocPricingTool } from "@/components/heloc/HelocPricingTool";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

const URL = "/dscr-heloc";
const HERO = getDscrClusterHero(URL);

const KEYWORDS = [
  "DSCR HELOC",
  "investment property HELOC",
  "rental property HELOC",
  "HELOC on investment property",
  "DSCR HELOAN",
  "home equity line of credit rental property",
  "investment property line of credit",
  "HELOC LLC owned property",
  "second lien investment property",
  "HELOC no income verification",
  "investment property HELOC requirements",
  "investment property HELOC rates",
  "HELOC vs cash out refinance rental",
].join(", ");

const FAQS = [
  {
    q: "Can you get a HELOC on an investment property?",
    a: "Yes. Investment-property HELOCs and closed-end home equity loans exist for non-owner-occupied rentals, though the credit and equity requirements are stricter than an owner-occupied line: generally 700+ FICO and up to 75% combined loan-to-value.",
  },
  {
    q: "What is the difference between a HELOC and a DSCR cash-out refinance?",
    a: "A HELOC sits behind your existing first mortgage as a revolving line you draw on as needed. A cash-out refinance replaces the first mortgage entirely and hands you a lump sum. If your first lien carries a low rate you want to keep, the HELOC preserves it.",
  },
  {
    q: "What are the credit and LTV requirements?",
    a: "Typically 700 minimum FICO and up to 75% CLTV including the first mortgage balance, with tighter limits on 2–4 unit properties and short-term rentals. Reserves are usually required.",
  },
  {
    q: "Can the property be held in an LLC?",
    a: "Entity-vested lines are available on select programs with a personal guaranty. Availability is narrower than on first-lien DSCR loans, so confirm before you plan around it.",
  },
  {
    q: "Is the rate fixed or variable?",
    a: "HELOCs are usually variable, indexed to Prime, with a draw period followed by a repayment period. Closed-end home equity loans (HELOANs) carry a fixed rate and a fixed term.",
  },
  {
    q: "What can the funds be used for?",
    a: "Business purposes only — acquisitions, rehab, reserves, or business debt. These lines may not be used for personal, family, or household purposes.",
  },
  {
    q: "Do I need to be an existing customer to get an investment property HELOC?",
    a: "No prior relationship is required. The line is underwritten independently on your credit, the property's equity position, and the existing first-lien balance, whether or not you financed the first mortgage through the same channel.",
  },
  {
    q: "Can I get a HELOC on a short-term rental property?",
    a: "Availability is narrower and combined loan-to-value limits are typically tighter than on a standard long-term rental, given the added income variability, so confirm eligibility for the specific property before applying.",
  },
  {
    q: "How long does it take to close an investment property HELOC?",
    a: "Timelines vary by program but often run comparable to or somewhat faster than a full refinance, since only a subordinate lien is being originated rather than a full first-lien payoff and re-underwrite.",
  },
  {
    q: "Is there a minimum draw amount on an investment property HELOC?",
    a: "Programs vary — some require an initial minimum draw at closing while others allow the line to remain fully undrawn. Check the specific program's terms before assuming full flexibility.",
  },
  {
    q: "Does drawing on my HELOC affect my DSCR on the first mortgage?",
    a: "No, the first mortgage's DSCR was set at its own closing and is not recalculated when you draw on a subordinate line, though your combined debt load and the property's overall leverage position do increase.",
  },
  {
    q: "Can I get a fixed-rate home equity loan instead of a variable HELOC?",
    a: "Yes, closed-end home equity loans (HELOANs) are available on select programs and carry a fixed rate and fixed term rather than the variable, revolving structure of a traditional HELOC.",
  },
];

export const Route = createFileRoute("/dscr-heloc")({
  head: () =>
    buildHead({
      title: "Investment Property HELOC & DSCR HELOAN — Draw on Rental Equity",
      description:
        "A revolving line of credit against rental equity: up to 75% CLTV, 700+ FICO, business-purpose only. Keep your low-rate first mortgage and draw only what you need.",
      url: URL,
      keywords: KEYWORDS,
      image: HERO?.src,
      imageAlt: HERO?.alt,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "DSCR Loans", url: "/dscr-loans" },
        { name: "Investment Property HELOC", url: URL },
      ],
      service: { name: "Investment Property HELOC and Home Equity Loan" },
      faqs: FAQS,
    }),
  component: DscrHeloc,
});

function DscrHeloc() {
  return (
    <SiteShell hideBreadcrumbs productLabel="Investor HELOC">
      <PageHero
        eyebrow="DSCR · Equity Line"
        title="Investment property HELOC & HELOAN"
        subtitle="Keep the low-rate first mortgage you already have and borrow against the equity behind it. A second-lien line lets you draw for the next deal, pay it back, and draw again — without refinancing the whole loan."
        image={HERO}
        primaryHref="/apply?product=heloc"
      />
      <RankKit page="dscr-heloc" part="answer" />
      <PageEssentials hideAnswer hideTldr hideToc />
      <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "DSCR Loans", href: "/dscr-loans" }, { name: "Investment Property HELOC" }]} />

      <TableOfContents
        items={[
          { href: "#tldr", label: "TL;DR" },
          { href: "#snapshot", label: "Snapshot" },
          { href: "#heloc-pricing", label: "HELOC pricing tool" },
          { href: "#heloc-vs-cashout", label: "HELOC vs. cash-out" },
          { href: "#second-lien", label: "Second-lien HELOC" },
          { href: "#use-cases", label: "Use cases" },
          { href: "#faq", label: "FAQ" },
        ]}
      />


      <TLDR
        id="tldr"
        points={[
          "An investment property HELOC is a revolving line secured by a rental — you draw, repay, and redraw instead of taking one lump sum.",
          "Typical parameters are 700+ FICO and up to 75% combined LTV on non-owner-occupied property.",
          "A line beats a cash-out when you need flexible, repeated capital for rehabs, earnest money, or short holds.",
          "You keep your existing low-rate first mortgage in place instead of refinancing it away.",
          "Line amounts, draw terms, and rates are illustrative and subject to underwriting — not a commitment to lend.",
        ]}
      />

      <Section id="snapshot">
        <StatRow
          items={[
            { label: "Max CLTV", value: "Up to 75%" },
            { label: "Min FICO", value: "700" },
            { label: "Lien position", value: "Second" },
            { label: "Purpose", value: "Business only" },
          ]}
        />
        <p className="mt-6 max-w-3xl text-muted-foreground">
          Combined loan-to-value counts your existing first mortgage plus the new line. On a $500,000 rental with a
          $260,000 first lien, a 75% CLTV ceiling puts the maximum line at roughly $115,000 before adjustments.
        </p>
      </Section>

      <HelocPricingTool
        id="heloc-pricing"
        surface="dscr-heloc-product-page"
        title="Price your investment property HELOC or home equity loan"
        intro="Enter your scenario to see matching first or second lien equity options from the current wholesale pricing schedule. Results are subject to change and are not an approval, commitment, or guarantee of financing."
      />

      <div className="bg-muted/40">
        <Section id="heloc-vs-cashout">

          <SectionHeader
            eyebrow="Pick the right tool"
            title="HELOC vs. cash-out refinance"
            intro="The deciding factor is usually the rate on your existing first mortgage and whether you need the money all at once."
          />
          <div className="mt-8 overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full min-w-[620px] text-sm">
              <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Factor</th>
                  <th className="px-4 py-3">Investment HELOC</th>
                  <th className="px-4 py-3">DSCR cash-out refinance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {[
                  ["First mortgage", "Untouched — keeps your existing rate", "Paid off and replaced"],
                  ["Access", "Revolving; draw and repay repeatedly", "One lump sum at closing"],
                  ["Rate type", "Usually variable (Prime-indexed)", "Fixed, IO, or ARM"],
                  ["Max leverage", "Up to 75% CLTV", "Up to 75% LTV"],
                  ["Credit floor", "700", "660"],
                  ["Best for", "Rolling rehab and acquisition capital", "One large draw, permanent structure"],
                ].map((row) => (
                  <tr key={row[0]}>
                    <td className="px-4 py-3 font-semibold text-foreground">{row[0]}</td>
                    <td className="px-4 py-3 text-brand">{row[1]}</td>
                    <td className="px-4 py-3 text-muted-foreground">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>

      <div className="container mx-auto max-w-7xl px-4">
        <ProductReportCtaBanner productKey="dscr" variant="inline" />
      </div>

      <LiveDscrRateSection
            strategyCards
        id="live-rates"
        eyebrow={"Live pricing · Equity access"}
        heading={"Investment Property HELOC Rates vs. DSCR Loan Rates"}
        paragraphs={[
            "An investment property HELOC is a revolving line, so its pricing works differently from a fixed DSCR term loan: the line rate is typically variable and tied to an index, while the DSCR options below are fixed-rate quotes priced live for a 30-year term.",
            "Showing both side by side is the point. If you intend to draw once and hold the balance for years, the fixed pricing below is the more direct comparison. If you need flexible access for the next acquisition or rehab, a line behaves differently over time — total interest depends on how much you draw and how long you carry it.",
        ]}
        faqs={[
            { q: "Do you publish live HELOC rates?", a: "No. Investment property line pricing is quoted per file rather than through the public pricing engine, so we do not publish a line rate here. The figures above are live fixed DSCR quotes shown for comparison only." },
            { q: "Can I hold both a DSCR first mortgage and a HELOC?", a: "Yes, subject to combined loan-to-value limits and the first lienholder's terms. Maximum CLTV varies by program and property profile." },
        ]}
        links={[
            { to: "/dscr-heloc-rates", label: "Price an investment property HELOC" },
            { to: "/dscr-second-lien-heloc", label: "See the DSCR second-lien HELOC product page" },
            { to: "/investment-property-rates", label: "Price a fixed DSCR alternative" },
            { to: "/dscr-cash-out-refinance", label: "Compare a DSCR cash-out refinance" },
            { to: "/dscr-loans", label: "Back to DSCR loan overview" },
        ]}
      />

      <Section id="second-lien">
        <SectionHeader
          eyebrow="Second position"
          title="Looking specifically for a 2nd lien HELOC?"
          intro="If you already have a first mortgage on the rental and want to keep it in place, the second-position version of this product has its own page with the combined loan-to-value math and a pricing tool preset to second lien."
        />
        <p className="mt-6 max-w-3xl text-muted-foreground">
          <a className="font-semibold text-brand underline underline-offset-4" href="/dscr-second-lien-heloc">
            Open the DSCR second-lien HELOC page
          </a>{" "}
          to price a second-position line on an investment property.
        </p>
      </Section>

      <Section id="use-cases">
        <SectionHeader eyebrow="How investors use it" title="Where a line beats a lump sum" />
        <div className="mt-8">
          <FeatureGrid
            items={[
              { title: "Down payment reserve", body: "Keep dry powder available so you can move on an off-market deal without liquidating other positions." },
              { title: "Rolling rehab budget", body: "Draw per project, repay after the refinance, then redraw on the next property." },
              { title: "Bridge to a purchase", body: "Buy with the line, then place permanent DSCR financing on the new property and repay the draw." },
              { title: "Portfolio liquidity", body: "Hold an undrawn line as a reserve buffer for vacancies and capital expenditure." },
            ]}
          />
        </div>
      </Section>

      <ProsCons
        title="Investment HELOC: benefits and trade-offs"
        pros={[
          "Preserves a low-rate first mortgage",
          "Interest accrues only on what you actually draw",
          "Reusable during the draw period",
          "Faster and cheaper to close than a full refinance",
        ]}
        cons={[
          "Higher credit floor (700+) than first-lien DSCR",
          "Variable rate — payments move with the index",
          "Lower availability on 2–4 unit and short-term rentals",
          "Entity-vested lines are offered by fewer investors",
        ]}
      />

      <InlineCTA
        variant="soft"
        title="See what line size your equity supports"
        body="Send the property address, estimated value, and current first-lien balance for a CLTV scenario."
        href="/apply?product=heloc"
      />


      <DscrProgramLinks currentPath={URL} />

      <RankKit page="dscr-heloc" part="details" />
      <Section id="faq">
        <SectionHeader eyebrow="FAQ" title="Investment property HELOC questions" />
        <div className="mt-8">
          <FAQ items={FAQS} />
        </div>
        <p className="mt-8 text-xs text-muted-foreground">
          Business-purpose loans only; proceeds may not be used for personal, family, or household purposes. Figures are
          illustrative, subject to change without notice, and are not a quote, offer, or commitment to lend. Program
          availability varies by state, lender, and property.
        </p>
      </Section>

      <DscrCrossProductLinks currentPath={URL} />

      <ExpertInsight insightKey="dscr-heloc" />

      <NetworkCallout
        title="Tapping equity in your own home instead?"
        body="This line of credit is secured by investment property. Homeowners 62 and older exploring equity in their primary residence can learn about reverse mortgages on our sister site."
        sites={["Simply Approved Reverse Mortgages"]}
      />

      <CTABand
        title="Unlock a line against your rental equity"
        body="Same-business-day scenario review on complete submissions."
        href="/apply?product=heloc"
      />
    <PageOutro hideInsight />
      </SiteShell>
  );
}
